import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { parseJsonArray, RECOMMENDATIONS, recruitErrorMessage } from './shared/recruitUi';

const { Text } = Typography;

const AssessmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.assessment(id || ''),
    queryFn: () => hrmsService.getAssessment(id!),
    enabled: Boolean(id),
  });
  const { data: templates = [] } = useQuery({
    queryKey: hrmsKeys.assessmentTemplates(),
    queryFn: () => hrmsService.listAssessmentTemplates(),
  });

  const template = useMemo(
    () => templates.find((t: any) => t.id === row?.template_id),
    [templates, row?.template_id]
  );
  const questions = parseJsonArray(template?.questions_json);

  useEffect(() => {
    if (!row || row.status === 'Completed') return;
    form.resetFields();
  }, [row?.id, row?.status, form]);

  const complete = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const responses = questions.map((q: any) => ({
        code: q.code,
        rating: Number(values.ratings?.[q.code] ?? 0),
        comment: values.comments?.[q.code],
      }));
      await hrmsService.completeAssessment(id!, {
        responses,
        recommendation: values.recommendation,
        feedback: values.feedback,
      });
      message.success('Assessment completed');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.assessment(id!) });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.assessments() });
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Assessment" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Assessment" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const completedItems = parseJsonArray((row.responses_json as any)?.items);

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Assessment"
          documentNumber={row.assessment_number}
          subtitle={row.title}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{row.status}</Tag>}
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/assessments')}>
                Back
              </Button>
              {row.status !== 'Completed' && (
                <Button type="primary" icon={<CheckOutlined />} loading={saving} onClick={complete}>
                  Complete scorecard
                </Button>
              )}
            </Space>
          }
        />
      }
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card size="small" title="Summary" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Type">{row.assessment_type}</Descriptions.Item>
              <Descriptions.Item label="Template">{template?.template_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Recommendation">{row.recommendation || '—'}</Descriptions.Item>
              <Descriptions.Item label="Result">
                {row.pass_fail ? (
                  <Tag color={row.pass_fail === 'Pass' ? 'green' : 'red'}>{row.pass_fail}</Tag>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Score" span={2}>
                {row.percentage_score != null ? (
                  <Progress percent={Number(row.percentage_score)} size="small" style={{ maxWidth: 280 }} />
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Feedback" span={2}>
                {row.feedback || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {row.status === 'Completed' ? (
            <Card size="small" title="Scorecard results">
              <Table
                rowKey="code"
                pagination={false}
                dataSource={completedItems}
                columns={[
                  { title: 'Criterion', dataIndex: 'code', render: (c: string) => questions.find((q: any) => q.code === c)?.text || c },
                  {
                    title: 'Rating',
                    dataIndex: 'rating',
                    width: 90,
                    render: (r: number, x: any) => `${r}/${questions.find((q: any) => q.code === x.code)?.max_rating || 5}`,
                  },
                  { title: 'Comment', dataIndex: 'comment' },
                ]}
              />
            </Card>
          ) : (
            <Card size="small" title="Scorecard">
              <Form form={form} layout="vertical">
                {questions.map((q: any) => (
                  <Row gutter={12} key={q.code} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={12}>
                      <Text strong>{q.text}</Text>
                      <div>
                        <Text type="secondary">
                          {q.section || 'General'} · weight {q.weight ?? 1}
                        </Text>
                      </div>
                    </Col>
                    <Col span={4}>
                      <Form.Item name={['ratings', q.code]} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                        <InputNumber min={1} max={q.max_rating || 5} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={['comments', q.code]} style={{ marginBottom: 0 }}>
                        <Input placeholder="Comment" />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
                <Form.Item name="recommendation" label="Recommendation" rules={[{ required: true }]}>
                  <Select options={RECOMMENDATIONS.map((r) => ({ value: r.value, label: r.label }))} />
                </Form.Item>
                <Form.Item name="feedback" label="Overall feedback">
                  <Input.TextArea rows={3} />
                </Form.Item>
              </Form>
            </Card>
          )}

          <div style={{ marginTop: 16 }}>
            <EntityAttachmentsCard
              entityType="HR_ASM"
              entityId={row.id}
              title="Assessment attachments"
              uploadHint="Attach rubrics, work samples, psychometric PDFs, or panel notes"
            />
          </div>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title="Linked records">
            {row.application_id ? (
              <Button type="link" onClick={() => navigate(`/hr/applications/${row.application_id}`)}>
                Open application
              </Button>
            ) : (
              <Text type="secondary">No application linked</Text>
            )}
            <div>
              <Button type="link" onClick={() => navigate(`/hr/candidates/${row.candidate_id}`)}>
                Open candidate
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default AssessmentDetailPage;
